import type {IPoint} from './point.js';
import {Point} from './point.js';
import {debounce} from '#utils/debounce.js';
import type {Nullable} from '#lit/types/general.js';

const MOVEMENT_TIMEOUT_MS = 100;

const INVALID_TARGET_TYPES = new Set<typeof Element>([
  HTMLSlotElement,
  HTMLMetaElement,
]);

const getPositionFromRect = (
  rect: Nullable<DOMRect>,
  position: Point
): Point => {
  if (!rect) return position;
  return Point.from(position.x - rect.left, position.y - rect.top);
};

type CursorRoot = Window | Document | ShadowRoot | HTMLElement;

export type CursorContext = {
  hasClicked: boolean;
  hasMoved: boolean;
  hasDragged: boolean;
  hasDropped: boolean;
};

export const CursorEventType = {
  DROP: 'drop',
  DRAG: 'drag',
  MOVE: 'move',
  CLICK: 'click',
  HOVER: 'hover',
  DOWN: 'down',
  UP: 'up',
  LEAVE: 'leave',
  ENTER: 'enter',
  WHEEL: 'wheel',
};
export type CursorEventType =
  (typeof CursorEventType)[keyof typeof CursorEventType];

export type CursorEvent = {
  type: CursorEventType;
  ctx: CursorContext;
  cursor: ICursor;
  native: Event | MouseEvent | WheelEvent;
  composedPath?: ICursor['composedPath'];
  dragPath?: ICursor['composedPath'];
  dragTarget?: Element;
  target?: Element;
};

export type CursorEventReturnOpts = {destroy: boolean};
export type CursorEventListener = (
  event: CursorEvent
) => void | CursorEventReturnOpts;
export type CursorEventPredicate = (event: CursorEvent) => boolean;

type MouseWindowListener =
  | EventListener
  | ((event: WheelEvent) => void)
  | ((event: MouseEvent) => void);

interface ICursor {
  root: CursorRoot;
  position: IPoint;
  composedPath: EventTarget[];
  withinRoot: boolean;

  diff?: Point;

  target(): Element | undefined;

  context(): Readonly<CursorContext>;

  when(predicate: CursorEventPredicate, hook: CursorEventListener): void;
}

export class Cursor implements ICursor {
  private static basePredicates: Record<CursorEventType, CursorEventPredicate> =
    (() => {
      const predicates: Record<CursorEventType, CursorEventPredicate> = {};

      for (const type of Object.values(CursorEventType)) {
        predicates[type] = (e: CursorEvent) => e.type === type;
      }

      return predicates;
    })();

  private static windowInstance?: Cursor;

  position = Point.from(0, 0);
  globalPosition = Point.from(0, 0);
  composedPath: EventTarget[] = [];
  withinRoot = this.root === window;

  private lastDraggedPath: EventTarget[] = [];

  private isMoving = false;
  private isMouseDown = false;
  private isDropping = false;
  private isDragging = false;
  private previousTarget?: Element;
  private previousPosition?: Point;

  private mouseEventMap: Map<keyof WindowEventMap, MouseWindowListener> =
    new Map([
      ['mousemove', this.onMouseMove],
      ['mousedown', this.onMouseDown],
      ['mouseup', this.onMouseUp],
      ['mouseenter', this.onMouseEnter],
      ['mouseleave', this.onMouseLeave],
      ['click', this.onMouseClick],
      ['wheel', this.onMouseWheel],
    ]);

  private listenerRegistry = new Map<
    CursorEventPredicate,
    Set<CursorEventListener>
  >();

  private timeoutIsMoving = debounce(
    () => (this.isMoving = false),
    MOVEMENT_TIMEOUT_MS
  );
  private timeoutIsDropping = debounce(
    () => (this.isDropping = false),
    MOVEMENT_TIMEOUT_MS
  );

  protected constructor(public root: CursorRoot) {
    for (const [event, handler] of this.mouseEventMap.entries()) {
      root.addEventListener(event, (handler as EventListener).bind(this));
    }

    for (const predicate of Object.values(Cursor.basePredicates)) {
      this.listenerRegistry.set(predicate, new Set<CursorEventListener>());
    }
  }

  static get window(): Cursor {
    this.windowInstance ??= Cursor.for(window);
    return this.windowInstance;
  }

  static for(root: CursorRoot) {
    return new Cursor(root);
  }

  private triggerListeners(
    type: CursorEventType,
    native: Event | MouseEvent | WheelEvent
  ) {
    const event: CursorEvent = {
      type,
      native,
      cursor: this,
      ctx: this.context(),
      target: this.target(),
      dragTarget: this.dragTarget(),
      composedPath: this.composedPath,
      dragPath: this.lastDraggedPath,
    };

    for (const [predicate, listeners] of this.listenerRegistry) {
      if (predicate(event)) {
        listeners.forEach(listener => {
          const opts = listener(event);
          if (opts?.destroy) listeners.delete(listener);
        });
      }
    }
  }

  private onMouseMove(event: MouseEvent) {
    const {x, y} = getPositionFromRect(
      this.rootBoundingRect(),
      Point.from(event.clientX, event.clientY)
    );

    this.previousPosition = Point.fromTuple(this.position.asTuple());
    this.position.set(x, y);
    this.globalPosition.set(event.clientX, event.clientY);
    this.isMoving = true;
    this.composedPath = event.composedPath();

    if (
      this.previousTarget &&
      !this.target()?.isSameNode(this.previousTarget)
    ) {
      this.triggerListeners(CursorEventType.HOVER, event);
    }

    if (!this.isDragging && this.isMouseDown) {
      this.lastDraggedPath = [...event.composedPath()];
    }

    if (this.isMouseDown) {
      this.isDragging = true;
      this.triggerListeners(CursorEventType.DRAG, event);
    }

    this.timeoutIsMoving();
    this.triggerListeners(CursorEventType.MOVE, event);

    this.previousTarget = this.target();
  }

  private onMouseUp(event: MouseEvent) {
    this.isDropping = this.isMouseDown && this.isDragging;
    this.isMouseDown = false;

    this.triggerListeners(CursorEventType.UP, event);

    if (this.isDropping) {
      this.isDragging = false;
      this.timeoutIsDropping();
      this.triggerListeners(CursorEventType.DROP, event);
    }
  }

  private onMouseDown(event: MouseEvent) {
    this.isMouseDown = true;
    this.triggerListeners(CursorEventType.DOWN, event);
  }

  private onMouseClick(event: MouseEvent) {
    this.isMouseDown = false;
    this.triggerListeners(CursorEventType.CLICK, event);
  }

  private onMouseEnter(event: MouseEvent) {
    this.withinRoot = true;
    this.triggerListeners(CursorEventType.ENTER, event);
  }

  private onMouseLeave(event: MouseEvent) {
    this.withinRoot = false;

    // FIXME(joshua): We might want to get rid of this at some point.
    this.onMouseUp(event);
    this.triggerListeners(CursorEventType.LEAVE, event);
  }

  private onMouseWheel(event: WheelEvent) {
    this.triggerListeners(CursorEventType.WHEEL, event);
  }

  private rootBoundingRect(): Nullable<DOMRect> {
    if (this.root instanceof HTMLElement) {
      return this.root.getBoundingClientRect();
    }

    return null;
  }

  get diff() {
    if (!this.previousPosition) return;
    return this.previousPosition.diff(this.position);
  }

  context() {
    return {
      hasDragged: this.isDragging,
      hasMoved: this.isMoving,
      hasDropped: this.isDropping,
      hasClicked: this.isMouseDown && !this.isMoving,
    } as const;
  }

  target() {
    return this.composedPath.find(target => {
      return !Array.from(INVALID_TARGET_TYPES).some(T => target instanceof T);
    }) as Element | undefined;
  }

  dragTarget() {
    return this.lastDraggedPath?.find(target => {
      return !Array.from(INVALID_TARGET_TYPES).some(T => target instanceof T);
    }) as Element | undefined;
  }

  on(type: CursorEventType, handler: CursorEventListener): Cursor {
    const predicate = Cursor.basePredicates[type];
    if (!this.listenerRegistry.has(predicate)) return this;

    this.listenerRegistry.get(predicate)!.add(handler);
    return this;
  }

  off(type: CursorEventType, handler?: CursorEventListener): Cursor {
    const predicate = Cursor.basePredicates[type];
    if (!this.listenerRegistry.has(predicate)) return this;

    if (handler) {
      this.listenerRegistry.get(predicate)!.delete(handler);
    } else {
      this.listenerRegistry.get(predicate)!.clear();
    }

    return this;
  }

  removeListener(type: CursorEventType, handler: CursorEventListener): Cursor {
    const predicate = Cursor.basePredicates[type];
    if (!this.listenerRegistry.has(predicate)) return this;

    this.listenerRegistry.get(predicate)!.delete(handler);
    return this;
  }

  when(predicate: CursorEventPredicate, handler: CursorEventListener) {
    if (this.listenerRegistry.has(predicate)) {
      return this.listenerRegistry.get(predicate)!.add(handler);
    }

    this.listenerRegistry.set(
      predicate,
      new Set<CursorEventListener>([handler])
    );
  }

  filter(predicate: (event: CursorEvent) => boolean) {
    return new Proxy(this, {
      get: function (cursor, prop, receiver) {
        if (prop === 'on') {
          return (type: CursorEventType, handler: CursorEventListener) => {
            const superPredicate = (event: CursorEvent) => {
              return predicate(event) && Cursor.basePredicates[type](event);
            };

            cursor.listenerRegistry.set(
              superPredicate,
              new Set<CursorEventListener>([handler])
            );
            return cursor;
          };
        }

        return Reflect.get(cursor, prop, receiver);
      },
    });
  }
}
